import React, { FunctionComponent, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { UI } from "@wwimmo/ui";
import { observer } from "mobx-react";
import { NewsStore } from "src/stores/NewsStore";
import { UIStore } from "src/stores/UIStore";
import styles from "../NewsEditForm.module.css";
import { useTranslation } from "react-i18next";

type NewsDocumentsSectionProps = {
    newsid: string;
    newsStore: NewsStore;
    uiStore: UIStore;
    onBeforeFileUpload?: () => Promise<boolean>;
};

const NewsDocumentsSectionBase: FunctionComponent<NewsDocumentsSectionProps> = ({
    newsid,
    newsStore,
    uiStore,
    onBeforeFileUpload
}) => {
    const { t } = useTranslation();

    const onExternalFileDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (onBeforeFileUpload) {
                const canProceed = await onBeforeFileUpload();
                if (!canProceed) return;
            }
            for (const file of acceptedFiles) {
                const newFileId = await newsStore.uploadExternalNewsFile(file, newsid);
                if (newFileId) {
                    uiStore.setDbNewsFiles([
                        ...uiStore.dbNewsFiles,
                        { __typename: "file_v_newsfiles" as const, id: newFileId, fileid: newFileId, name: file.name }
                    ]);
                }
            }
        },
        [newsStore, newsid, uiStore, onBeforeFileUpload]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: onExternalFileDrop,
        accept: {
            // Documents
            "application/pdf": [".pdf"],
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "application/vnd.ms-powerpoint": [".ppt"],
            "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
            "text/plain": [".txt"],
            "text/csv": [".csv"],
            // Images
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "image/gif": [".gif"],
            "image/webp": [".webp"],
            "image/svg+xml": [".svg"],
            // Archives
            "application/zip": [".zip"],
            "application/x-rar-compressed": [".rar"]
        }
    });

    // Drop zone for files from the user's computer. Documents already in the portal are chosen in a modal.
    return (
        <div
            {...getRootProps({
                className: `${styles.DocumentTile} ${styles.DocumentTileUpload} ${isDragActive ? styles.DocumentTileActive : ""}`
            })}
        >
            <input {...getInputProps()} />
            <UI.Icon icon={UI.SVGIcon.PaperclipAdd} size={24} className={styles.DocumentTileIcon} />
            <div className={styles.DocumentTileTitle}>{t("screens.realestate.news_edit.upload_documents_title")}</div>
            <div className={styles.DocumentTileHint}>{t("screens.realestate.news_edit.upload_documents_hint")}</div>
        </div>
    );
};

export const NewsDocumentsSection = observer(NewsDocumentsSectionBase);
